import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle, XCircle, ArrowLeft, Clock, FileText, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

const TestArticleGeneration = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);
  const { toast } = useToast();

  const runTest = async () => {
    setIsRunning(true);
    setTestResults(null);

    try {
      toast({
        title: "Starting Test",
        description: "This may take up to 60 seconds...",
      });

      const { data, error } = await supabase.functions.invoke('test-generate-article', {
        body: {},
      });

      // Handle Supabase function errors with detailed messages
      if (error) {
        console.error('Supabase function error:', error);
        
        // Extract detailed error information
        let errorMessage = error.message || "Failed to run test";
        let errorDetails = "";
        
        // If we have data even with an error, it might contain more details
        if (data && typeof data === 'object') {
          errorMessage = data.error || errorMessage;
          errorDetails = data.details || "";
        }
        
        toast({
          title: "Test Error",
          description: errorMessage,
          variant: "destructive",
        });
        
        setTestResults({
          success: false,
          error: errorMessage,
          details: errorDetails,
          errors: [errorMessage + (errorDetails ? `: ${errorDetails}` : "")],
        });
        setIsRunning(false);
        return;
      }

      // Handle successful response
      setTestResults(data);
      
      if (data?.success) {
        toast({
          title: "Test Passed! ✅",
          description: `Generated ${data.articles?.length || 0} articles successfully`,
        });
      } else {
        // Test ran but failed validation
        const errorMsg = data?.error || "Test validation failed";
        toast({
          title: "Test Failed",
          description: errorMsg,
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error('Unexpected test error:', error);
      
      // Extract as much detail as possible
      let errorMessage = "An unexpected error occurred";
      let errorDetails = "";
      
      if (error.message) {
        errorMessage = error.message;
      }
      
      if (error.context) {
        errorDetails = JSON.stringify(error.context);
      }
      
      toast({
        title: "Unexpected Error",
        description: errorMessage,
        variant: "destructive",
      });
      
      setTestResults({
        success: false,
        error: errorMessage,
        details: errorDetails,
        errors: [errorMessage + (errorDetails ? ` | Context: ${errorDetails}` : "")],
      });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-4 py-6">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2 mb-4">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            🧪 Article Generation Test Suite
          </h1>
          <p className="text-muted-foreground">
            Test the Anthropic-powered article generation system with mock data
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Control Panel */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Test Controls</CardTitle>
            <CardDescription>
              This test uses mock market data and calls the Anthropic API to generate real articles
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={runTest}
              disabled={isRunning}
              size="lg"
              className="w-full sm:w-auto"
            >
              {isRunning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Running Test... (may take 60s)
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Run Article Generation Test
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Test Results */}
        {testResults && (
          <>
            {/* Summary */}
            <Card className={`mb-8 border-2 ${testResults.success ? 'border-green-500' : 'border-red-500'}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    {testResults.success ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-600" />
                    )}
                    Test Results
                  </CardTitle>
                  <Badge variant={testResults.success ? "default" : "destructive"}>
                    {testResults.success ? "PASSED" : "FAILED"}
                  </Badge>
                </div>
                <CardDescription className="text-xs">
                  Test ID: {testResults.test_run_id}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Performance Metrics */}
                {testResults.performance_metrics && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Total Time</p>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <p className="text-sm font-semibold">
                          {(testResults.performance_metrics.total_time_ms / 1000).toFixed(1)}s
                        </p>
                        {testResults.performance_metrics.meets_time_requirement ? (
                          <Badge variant="outline" className="text-xs">{"< 30s ✓"}</Badge>
                        ) : (
                          <Badge variant="destructive" className="text-xs">{"> 30s ✗"}</Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">API Response</p>
                      <p className="text-sm font-semibold">
                        {(testResults.performance_metrics.api_response_time_ms / 1000).toFixed(1)}s
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Tokens Used</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold">
                          {testResults.performance_metrics.tokens_used}
                        </p>
                        {testResults.performance_metrics.meets_token_requirement ? (
                          <Badge variant="outline" className="text-xs">{"< 4K ✓"}</Badge>
                        ) : (
                          <Badge variant="destructive" className="text-xs">{"> 4K ✗"}</Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Articles Generated</p>
                      <p className="text-sm font-semibold">
                        {testResults.articles?.length || 0}
                      </p>
                    </div>
                  </div>
                )}

                {/* Errors */}
                {(testResults.error || (testResults.errors && testResults.errors.length > 0)) && (
                  <div className="space-y-3">
                    {/* Main error message */}
                    {testResults.error && (
                      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <h4 className="font-semibold text-red-600 mb-2 flex items-center gap-2">
                          <XCircle className="w-4 h-4" />
                          Error
                        </h4>
                        <p className="text-sm text-red-600 font-medium">{testResults.error}</p>
                        {testResults.details && (
                          <details className="mt-3">
                            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                              View technical details
                            </summary>
                            <pre className="mt-2 text-xs p-3 bg-muted/50 rounded overflow-x-auto">
                              {testResults.details}
                            </pre>
                          </details>
                        )}
                      </div>
                    )}
                    
                    {/* Additional errors list */}
                    {testResults.errors && testResults.errors.length > 0 && (
                      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <h4 className="font-semibold text-red-600 mb-2">Additional Errors:</h4>
                        <ul className="text-sm space-y-1">
                          {testResults.errors.map((error: string, idx: number) => (
                            <li key={idx} className="text-red-600">• {error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Articles */}
            {testResults.articles && testResults.articles.map((article: any, idx: number) => (
              <Card key={idx} className="mb-6">
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">{article.title}</CardTitle>
                      <CardDescription>{article.summary}</CardDescription>
                    </div>
                    <Badge variant="outline">{article.category}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Article Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4 bg-muted/30 rounded-lg text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Word Count</p>
                      <div className="flex items-center gap-2">
                        <FileText className="w-3 h-3" />
                        <p className="font-semibold">{article.word_count}</p>
                        {article.word_count >= 400 && article.word_count <= 650 ? (
                          <span className="text-green-600 text-xs">✓</span>
                        ) : (
                          <span className="text-red-600 text-xs">✗</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Read Time</p>
                      <p className="font-semibold">{article.estimated_read_time} min</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">CoinSpot Link</p>
                      <p className="font-semibold">{article.has_coinspot_link ? '✓' : '✗'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Swyftx Link</p>
                      <p className="font-semibold">{article.has_swyftx_link ? '✓' : '✗'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Disclosure</p>
                      <p className="font-semibold">{article.has_disclosure ? '✓' : '✗'}</p>
                    </div>
                  </div>

                  {/* Validation Results */}
                  {testResults.validation_results && testResults.validation_results[idx] && (
                    <div className={`p-4 rounded-lg border ${
                      testResults.validation_results[idx].valid 
                        ? 'bg-green-500/10 border-green-500/20' 
                        : 'bg-yellow-500/10 border-yellow-500/20'
                    }`}>
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        {testResults.validation_results[idx].valid ? (
                          <>
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-green-600">Validation Passed</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 text-yellow-600" />
                            <span className="text-yellow-600">Validation Issues</span>
                          </>
                        )}
                      </h4>
                      {testResults.validation_results[idx].errors?.length > 0 && (
                        <ul className="text-sm space-y-1">
                          {testResults.validation_results[idx].errors.map((error: string, i: number) => (
                            <li key={i} className="text-yellow-700">• {error}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* Content Preview */}
                  <div>
                    <h4 className="font-semibold mb-2">Content Preview (First 500 chars):</h4>
                    <div className="p-4 bg-muted/30 rounded-lg text-sm font-mono whitespace-pre-wrap">
                      {article.content.substring(0, 500)}...
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        )}

        {/* Instructions */}
        {!testResults && !isRunning && (
          <Card>
            <CardHeader>
              <CardTitle>About This Test</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">What This Test Does:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>✓ Uses mock crypto market data (BTC, ETH, SOL, ADA, DOT)</li>
                  <li>✓ Calls Anthropic API (Claude Sonnet 4) with real prompt</li>
                  <li>✓ Generates 2 complete articles (550-650 words each)</li>
                  <li>✓ Validates content quality and structure</li>
                  <li>✓ Checks affiliate link injection</li>
                  <li>✓ Measures performance metrics</li>
                  <li>✓ Tests Australian spelling and formatting</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Expected Results:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Response time: Should be under 30 seconds (currently ~51s)</li>
                  <li>• Token usage: Should be under 4,000 tokens</li>
                  <li>• Word count: 400-650 words per article</li>
                  <li>• Contains proper headers (##) and formatting</li>
                  <li>• Includes CoinSpot and Swyftx mentions with affiliate links</li>
                  <li>• Includes disclosure statement</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TestArticleGeneration;
